import { queryClaudeWithContext, THINKING_MODE_TEMPERATURE } from "../../../../services/claude";
import { INITIAL_INDEX, NOT_FOUND_INDEX } from "../../constants/numeric";
import { getEnlightenMeSystemPrompt } from "../../utils/ragchatStorage";
import { AnswerMode, ChatActions, ChatGetState, ChatMessage, ChatSetState, ChatState, MessageHistory } from "../chatTypes";

type MessageActions = Pick<
  ChatActions,
  | "addMessage"
  | "updateMessage"
  | "setMessages"
  | "addToHistory"
  | "resetMessages"
  | "handleEditMessage"
  | "truncateMessagesAfter"
  | "cancelEditMessage"
  | "sendMessage"
>;

const MIN_TEMPERATURE = 0;
const MAX_TEMPERATURE = 1;
const REQUEST_CANCELED_MESSAGE = "Request was canceled";

const MODE_PROMPTS: Record<Exclude<AnswerMode, "none">, string> = {
  quick: `You are helping the user explore call data for patterns and points of interest.
Surface observations quickly. Use brief bullets. Flag things worth investigating
further. Speculation is welcome but label it clearly (e.g. "Worth checking:").
Do not over-qualify or hedge. Prioritise signal over completeness.`,
  deep: `You are helping the user build a thorough answer to a specific question. Be
comprehensive. Structure your response with clear sections where helpful.
Reference specific evidence from the source material. Label any inference or
speculation clearly. Prioritise accuracy and completeness over brevity.`
};

export const buildQueryWithModeInjection = (
  content: string,
  answerMode: AnswerMode,
  lastInjectedMode: AnswerMode | null
): { query: string; injected: boolean } => {
  if (answerMode === "none") {
    return { query: content, injected: false };
  }
  if (lastInjectedMode === answerMode) {
    return { query: content, injected: false };
  }
  const modePrompt = MODE_PROMPTS[answerMode];
  return {
    query: `## Analysis Mode\n${modePrompt}\n\n## Question\n${content}`,
    injected: true
  };
};

const isCustomTemperatureValid = (value: number | undefined): value is number =>
  value !== undefined && value >= MIN_TEMPERATURE && value <= MAX_TEMPERATURE;

const buildUserMessage = (content: string, state: ChatState, customTemperature: number | undefined): ChatMessage => ({
  id: crypto.randomUUID(),
  content,
  role: "user",
  timestamp: new Date(),
  temperature: customTemperature ?? state.temperature,
  includedArtifactTypes: [...state.includedArtifactTypes],
  answerMode: state.answerMode
});

const buildAssistantMessage = (state: ChatState): { id: string; message: ChatMessage } => {
  const id = crypto.randomUUID();
  return {
    id,
    message: {
      id,
      content: "",
      role: "assistant",
      timestamp: new Date(),
      isStreaming: true,
      isThinking: state.thinkingMode,
      thinkingContent: ""
    }
  };
};

const isCanceledError = (error: unknown): boolean =>
  error instanceof Error && error.message === REQUEST_CANCELED_MESSAGE;

interface SendMessageParams {
  content: string;
  customTemperature?: number;
}

interface ExecuteQueryParams {
  injectedQuery: string;
  customTemperature: number | undefined;
  assistantMessageId: string;
}

const executeQuery = async (get: ChatGetState, params: ExecuteQueryParams): Promise<void> => {
  const { injectedQuery, customTemperature, assistantMessageId } = params;
  const state = get();
  const temperature = state.thinkingMode ? THINKING_MODE_TEMPERATURE : (customTemperature ?? state.temperature);

  try {
    const result = await queryClaudeWithContext({
      query: injectedQuery,
      context: state.contextData ?? [],
      messages: state.messageHistory,
      systemPrompt: getEnlightenMeSystemPrompt(),
      temperature,
      maxTokens: state.maxTokens,
      budgetTokens: state.budgetTokens,
      effort: state.effort,
      model: state.model,
      thinkingMode: state.thinkingMode,
      onMessageDelta: (delta: string) => get().handleStreamUpdate(delta),
      onThinkingDelta: (delta: string, type: string) => get().handleThinkingDelta(delta, type)
    });

    if (result.ok) {
      get().updateMessage(assistantMessageId, {
        content: result.response ?? "",
        isStreaming: false
      });
      get().addToHistory({ role: "assistant", content: result.response ?? "" });
      return;
    }
    if (isCanceledError(result.error)) {
      return;
    }
    throw result.error;
  } catch (error) {
    if (isCanceledError(error)) {
      return;
    }
    const errorMessage = error instanceof Error ? error.message : "An error occurred processing your request";
    get().addMessage({
      id: crypto.randomUUID(),
      content: `Error: ${errorMessage}`,
      role: "error",
      timestamp: new Date()
    });
  } finally {
    get().handleComplete();
    get().setLoading(false);
  }
};

const executeSendMessage = async (set: ChatSetState, get: ChatGetState, params: SendMessageParams): Promise<void> => {
  const { content, customTemperature } = params;
  const state = get();

  if (isCustomTemperatureValid(customTemperature)) {
    get().setTemperature(customTemperature);
  }
  if (!content.trim() || state.loading) {
    return;
  }

  get().setLoading(true);

  if (state.editingMessage) {
    get().truncateMessagesAfter(state.editingMessage);
    set({ editingMessage: null });
  }

  const userMessage = buildUserMessage(content, state, customTemperature);
  const { query: injectedQuery, injected } = buildQueryWithModeInjection(
    content,
    state.answerMode,
    state.lastInjectedAnswerMode
  );

  if (injected || state.answerMode === "none") {
    set({ lastInjectedAnswerMode: state.answerMode });
  }

  get().addMessage(userMessage);
  get().addToHistory({ role: "user", content: injectedQuery });

  const { id: assistantMessageId, message: assistantMessage } = buildAssistantMessage(state);
  get().addMessage(assistantMessage);

  await executeQuery(get, { injectedQuery, customTemperature, assistantMessageId });

  set({ inputValue: "" });
};

export const createMessageActions = (set: ChatSetState, get: ChatGetState): MessageActions => ({
  addMessage: (message: ChatMessage): void => {
    set((state) => ({ messages: [...state.messages, message] }));
  },

  updateMessage: (messageId: string, updates: Partial<ChatMessage>): void => {
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === messageId ? { ...msg, ...updates } : msg
      )
    }));
  },

  setMessages: (messages: ChatMessage[]): void => {
    set({ messages });
  },

  addToHistory: (entry: MessageHistory): void => {
    set((state) => ({ messageHistory: [...state.messageHistory, entry] }));
  },

  resetMessages: (): void => {
    set({ messages: [], messageHistory: [], lastInjectedAnswerMode: null });
  },

  handleEditMessage: (message: ChatMessage): void => {
    set({ inputValue: message.content, editingMessage: message.id });
  },

  truncateMessagesAfter: (messageId: string): void => {
    set((state) => {
      const messageIndex = state.messages.findIndex((msg) => msg.id === messageId);
      return messageIndex !== NOT_FOUND_INDEX
        ? { messages: state.messages.slice(INITIAL_INDEX, messageIndex) }
        : state;
    });
  },

  cancelEditMessage: (): void => {
    set({ inputValue: "", editingMessage: null });
  },

  sendMessage: async (content: string, customTemperature?: number): Promise<void> => {
    await executeSendMessage(set, get, { content, customTemperature });
  }
});
